import {
  collection,
  doc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

const PLAYERS_COLLECTION = 'players';
const MATCHES_COLLECTION = 'matches';
const TOURNAMENTS_COLLECTION = 'tournaments';
const USERNAMES_COLLECTION = 'usernames';
const PLAYER_PROFILES_COLLECTION = 'playerProfiles';
const PAST_CHAMPIONS_COLLECTION = 'pastChampions';

const USERNAME_PATTERN = /^[a-zA-Z0-9_]{3,20}$/;

export const MAX_PLAYERS = 8;
export const ROUND_SECONDS = 10;
const ROUND_MS = ROUND_SECONDS * 1000;

const BEATS = { rock: 'scissors', paper: 'rock', scissors: 'paper' };

export const BOT_DIFFICULTIES = ['easy', 'medium', 'hard'];
const BOT_NAMES = ['Byte', 'Chip', 'Circuit', 'Pixel', 'Volt', 'Nano', 'Turbo', 'Ziggy'];
const emptyMoveCounts = () => ({ rock: 0, paper: 0, scissors: 0 });

function resolveThrow(choice1, choice2) {
  if (choice1 === choice2) return 'tie';
  return BEATS[choice1] === choice2 ? 'player1' : 'player2';
}

function shuffle(items) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

async function findOpenTournamentByRoomCode(roomCode) {
  const snapshot = await getDocs(
    query(
      collection(db, TOURNAMENTS_COLLECTION),
      where('roomCode', '==', roomCode.trim().toUpperCase()),
      where('status', '==', 'lobby')
    )
  );
  return snapshot.empty ? null : snapshot.docs[0];
}

async function generateUniqueRoomCode() {
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = `RPS-${Math.floor(100 + Math.random() * 900)}`;
    if (!(await findOpenTournamentByRoomCode(code))) return code;
  }
  return `RPS-${Math.floor(1000 + Math.random() * 9000)}`;
}

// Pairs up players into best-of-3 matches for the given round. An odd
// player out gets a bye and auto-advances as a completed match.
function pairUpPlayers(players, round, tournamentId) {
  const matches = [];
  for (let i = 0; i < players.length; i += 2) {
    const p1 = players[i];
    const p2 = players[i + 1];

    if (!p2) {
      matches.push({
        tournamentId,
        round,
        player1Id: p1.id,
        player1Name: p1.name,
        player1IsBot: !!p1.isBot,
        player1ProfileId: p1.profileId ?? null,
        player2Id: null,
        player2Name: null,
        player2IsBot: false,
        player2ProfileId: null,
        score1: 0,
        score2: 0,
        choice1: null,
        choice2: null,
        gameNumber: 1,
        status: 'complete',
        winnerId: p1.id,
        winnerName: p1.name,
        isBye: true,
        moveDeadline: null,
      });
      continue;
    }

    matches.push({
      tournamentId,
      round,
      player1Id: p1.id,
      player1Name: p1.name,
      player1IsBot: !!p1.isBot,
      player1ProfileId: p1.profileId ?? null,
      player2Id: p2.id,
      player2Name: p2.name,
      player2IsBot: !!p2.isBot,
      player2ProfileId: p2.profileId ?? null,
      score1: 0,
      score2: 0,
      choice1: null,
      choice2: null,
      gameNumber: 1,
      status: 'pending',
      winnerId: null,
      winnerName: null,
      isBye: false,
      moveDeadline: Date.now() + ROUND_MS,
    });
  }
  return matches;
}

// Creates a brand new tournament (room) and joins its creator as host.
// Returns the new player's identity, including which tournament it belongs to.
export async function createTournament(hostName, profileId) {
  const roomCode = await generateUniqueRoomCode();
  const tournamentRef = doc(collection(db, TOURNAMENTS_COLLECTION));
  const playerRef = doc(collection(db, PLAYERS_COLLECTION));

  const batch = writeBatch(db);
  batch.set(tournamentRef, {
    roomCode,
    name: `${hostName}'s Game`,
    hostId: playerRef.id,
    hostName,
    status: 'lobby',
    maxPlayers: MAX_PLAYERS,
    playerCount: 1,
    currentRound: null,
    winnerId: null,
    winnerName: null,
    results: [],
    pointsProcessed: false,
    createdAt: serverTimestamp(),
  });
  batch.set(playerRef, {
    tournamentId: tournamentRef.id,
    name: hostName,
    isHost: true,
    isBot: false,
    profileId: profileId ?? null,
    ready: false,
    moveCounts: emptyMoveCounts(),
    joinedAt: serverTimestamp(),
  });
  await batch.commit();

  return { id: playerRef.id, name: hostName, tournamentId: tournamentRef.id };
}

// Joins an existing open tournament by its Firestore id.
// Throws if the lobby no longer exists, has started, or is full.
export async function joinTournamentById(tournamentId, name, profileId) {
  const tournamentRef = doc(db, TOURNAMENTS_COLLECTION, tournamentId);
  const playerRef = doc(collection(db, PLAYERS_COLLECTION));

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(tournamentRef);
    if (!snap.exists() || snap.data().status !== 'lobby') {
      throw new Error('This lobby is no longer open.');
    }
    const data = snap.data();
    if (data.playerCount >= data.maxPlayers) {
      throw new Error('This lobby is full.');
    }

    transaction.set(playerRef, {
      tournamentId,
      name,
      isHost: false,
      isBot: false,
      profileId: profileId ?? null,
      ready: false,
      moveCounts: emptyMoveCounts(),
      joinedAt: serverTimestamp(),
    });
    transaction.update(tournamentRef, { playerCount: data.playerCount + 1 });
  });

  return { id: playerRef.id, name, tournamentId };
}

// Joins an existing open tournament by its shareable room code (e.g. RPS-447).
export async function joinTournamentByCode(roomCode, name, profileId) {
  const tournamentDoc = await findOpenTournamentByRoomCode(roomCode);
  if (!tournamentDoc) {
    throw new Error('No open lobby found with that room code.');
  }
  return joinTournamentById(tournamentDoc.id, name, profileId);
}

// Fills every open seat in a lobby with bot players, spreading them evenly
// across the three difficulty levels. Bots are marked ready immediately so
// they never block the host from starting.
export async function fillWithBots(tournamentId, seatsToFill) {
  const batch = writeBatch(db);
  const usedNames = new Set();

  for (let i = 0; i < seatsToFill; i++) {
    const difficulty = BOT_DIFFICULTIES[i % BOT_DIFFICULTIES.length];
    let name;
    do {
      name = BOT_NAMES[Math.floor(Math.random() * BOT_NAMES.length)];
    } while (usedNames.has(name) && usedNames.size < BOT_NAMES.length);
    usedNames.add(name);

    const playerRef = doc(collection(db, PLAYERS_COLLECTION));
    batch.set(playerRef, {
      tournamentId,
      name: `${name} 🤖`,
      isHost: false,
      isBot: true,
      profileId: null,
      difficulty,
      ready: true,
      moveCounts: emptyMoveCounts(),
      joinedAt: serverTimestamp(),
    });
  }

  batch.update(doc(db, TOURNAMENTS_COLLECTION, tournamentId), { playerCount: increment(seatsToFill) });
  await batch.commit();
}

// Subscribes to every currently-open (joinable) tournament.
// Returns an unsubscribe function.
export function subscribeToOpenLobbies(callback, onError) {
  const openQuery = query(collection(db, TOURNAMENTS_COLLECTION), where('status', '==', 'lobby'));
  return onSnapshot(
    openQuery,
    (snapshot) => {
      const lobbies = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      callback(lobbies);
    },
    onError
  );
}

// Subscribes to the live list of players in one tournament, ordered by join time.
// Returns an unsubscribe function.
export function subscribeToPlayers(tournamentId, callback, onError) {
  const playersQuery = query(collection(db, PLAYERS_COLLECTION), where('tournamentId', '==', tournamentId));
  return onSnapshot(
    playersQuery,
    (snapshot) => {
      const players = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => (a.joinedAt?.toMillis?.() ?? 0) - (b.joinedAt?.toMillis?.() ?? 0));
      callback(players);
    },
    onError
  );
}

// Toggles a player's ready-to-start status in the lobby.
export async function setReady(playerId, ready) {
  await updateDoc(doc(db, PLAYERS_COLLECTION, playerId), { ready });
}

// Removes a player from the roster, e.g. after they've been eliminated and
// want to leave rather than stick around for the rest of the tournament.
export async function leaveLobby(playerId, tournamentId) {
  const playerRef = doc(db, PLAYERS_COLLECTION, playerId);
  const tournamentRef = doc(db, TOURNAMENTS_COLLECTION, tournamentId);

  await runTransaction(db, async (transaction) => {
    const tournamentSnap = await transaction.get(tournamentRef);
    transaction.delete(playerRef);
    if (tournamentSnap.exists()) {
      const data = tournamentSnap.data();
      // The host leaving before the tournament starts orphans the lobby —
      // no one else can manage or start it — so tear it down instead of
      // just decrementing the count. Once the bracket is running, other
      // players are mid-match, so the host forfeiting is treated like any
      // other player leaving instead.
      if (data.status === 'lobby' && data.hostId === playerId) {
        transaction.delete(tournamentRef);
      } else {
        transaction.update(tournamentRef, { playerCount: Math.max(0, (data.playerCount || 1) - 1) });
      }
    }
  });
}

// A player forfeiting while they still have a live match in the current
// round. Hands the win to their opponent so the round can still complete
// instead of leaving it stuck waiting on someone who's gone, then removes
// the forfeiting player from the roster.
export async function forfeitMatch(playerId, tournamentId, matchId) {
  const playerRef = doc(db, PLAYERS_COLLECTION, playerId);
  const tournamentRef = doc(db, TOURNAMENTS_COLLECTION, tournamentId);
  const matchRef = doc(db, MATCHES_COLLECTION, matchId);

  await runTransaction(db, async (transaction) => {
    const matchSnap = await transaction.get(matchRef);
    const tournamentSnap = await transaction.get(tournamentRef);

    if (matchSnap.exists()) {
      const match = matchSnap.data();
      if (match.status !== 'complete') {
        const isPlayer1 = match.player1Id === playerId;
        transaction.update(matchRef, {
          status: 'complete',
          winnerId: isPlayer1 ? match.player2Id : match.player1Id,
          winnerName: isPlayer1 ? match.player2Name : match.player1Name,
          choice1: null,
          choice2: null,
          moveDeadline: null,
          resolvedAt: Date.now(),
        });
      }
    }

    transaction.delete(playerRef);
    if (tournamentSnap.exists()) {
      const data = tournamentSnap.data();
      transaction.update(tournamentRef, { playerCount: Math.max(0, (data.playerCount || 1) - 1) });
    }
  });
}

// Subscribes to one tournament's live state.
// Returns an unsubscribe function.
export function subscribeToTournament(tournamentId, callback, onError) {
  return onSnapshot(
    doc(db, TOURNAMENTS_COLLECTION, tournamentId),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onError
  );
}

// Subscribes to every match belonging to one tournament, sorted by round.
// Returns an unsubscribe function.
export function subscribeToMatches(tournamentId, callback, onError) {
  const matchesQuery = query(collection(db, MATCHES_COLLECTION), where('tournamentId', '==', tournamentId));
  return onSnapshot(
    matchesQuery,
    (snapshot) => {
      const matches = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => a.round - b.round);
      callback(matches);
    },
    onError
  );
}

// Seeds players into a round-1 bracket and marks the tournament in-progress.
// Should only be called by the host.
export async function startTournament(players, tournamentId) {
  const batch = writeBatch(db);
  const matches = pairUpPlayers(shuffle(players), 1, tournamentId);

  matches.forEach((match) => {
    const matchRef = doc(collection(db, MATCHES_COLLECTION));
    batch.set(matchRef, match);
  });

  batch.set(
    doc(db, TOURNAMENTS_COLLECTION, tournamentId),
    { status: 'in-progress', currentRound: 1, startedAt: serverTimestamp() },
    { merge: true }
  );

  await batch.commit();
}

// Submits a player's rock/paper/scissors throw for the current game in a
// match. Resolves the game atomically once both players have thrown, and
// completes the match once a player reaches 2 wins.
export async function submitChoice(matchId, playerId, choice) {
  const matchRef = doc(db, MATCHES_COLLECTION, matchId);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(matchRef);
    if (!snap.exists()) return;
    const match = snap.data();
    if (match.status === 'complete') return;

    const isPlayer1 = match.player1Id === playerId;
    const isPlayer2 = match.player2Id === playerId;
    if (!isPlayer1 && !isPlayer2) return;
    if ((isPlayer1 && match.choice1) || (isPlayer2 && match.choice2)) return;

    const choice1 = isPlayer1 ? choice : match.choice1;
    const choice2 = isPlayer2 ? choice : match.choice2;
    const update = isPlayer1 ? { choice1: choice } : { choice2: choice };

    if (choice1 && choice2) {
      const result = resolveThrow(choice1, choice2);
      update.choice1 = null;
      update.choice2 = null;
      update.lastChoice1 = choice1;
      update.lastChoice2 = choice2;
      update.lastResult = result;
      update.resolvedAt = Date.now();

      if (result !== 'tie') {
        const score1 = match.score1 + (result === 'player1' ? 1 : 0);
        const score2 = match.score2 + (result === 'player2' ? 1 : 0);
        update.score1 = score1;
        update.score2 = score2;

        if (score1 === 2 || score2 === 2) {
          update.status = 'complete';
          update.winnerId = score1 === 2 ? match.player1Id : match.player2Id;
          update.winnerName = score1 === 2 ? match.player1Name : match.player2Name;
          update.moveDeadline = null;
        } else {
          update.gameNumber = match.gameNumber + 1;
          update.moveDeadline = Date.now() + ROUND_MS;
        }
      } else {
        update.moveDeadline = Date.now() + ROUND_MS;
      }
    }

    transaction.update(matchRef, update);
    transaction.update(doc(db, PLAYERS_COLLECTION, playerId), { [`moveCounts.${choice}`]: increment(1) });
  });
}

// Permanently reserves a (case-insensitive) username for a uid, via a doc
// at usernames/{lowercased}. Throws if it's already taken by a different
// uid. No release/rename path in v1 — see docs/auth-leaderboard-plan.md.
export async function reserveUsername(uid, username) {
  const normalized = username.trim().toLowerCase();
  if (!USERNAME_PATTERN.test(normalized)) {
    throw new Error('Username must be 3-20 characters: letters, numbers, and underscores only.');
  }

  const usernameRef = doc(db, USERNAMES_COLLECTION, normalized);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(usernameRef);
    if (snap.exists()) {
      if (snap.data().uid !== uid) throw new Error('That username is already taken.');
      return;
    }
    transaction.set(usernameRef, { uid, createdAt: serverTimestamp() });
  });
}

// One-time fetch of the top N registered players by total points, for the
// global leaderboard screen. Anonymous profiles are excluded server-side
// (isRegistered != true) — see docs/auth-leaderboard-plan.md.
export async function fetchLeaderboard(limitCount = 50) {
  const snapshot = await getDocs(
    query(
      collection(db, PLAYER_PROFILES_COLLECTION),
      where('isRegistered', '==', true),
      orderBy('totalPoints', 'desc'),
      limit(limitCount)
    )
  );
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}

// Closed weeks in reverse-chronological order, each with its snapshotted
// top 3 (uid, displayName, weeklyScore, rank). Written once per week by
// recomputeWeeklyLeaderboardScheduled in functions/index.js — this is a
// read-only view of that archive, entirely separate from the lifetime
// leaderboard above.
export async function fetchPastChampions(limitCount = 26) {
  const snapshot = await getDocs(
    query(collection(db, PAST_CHAMPIONS_COLLECTION), orderBy('closedAt', 'desc'), limit(limitCount))
  );
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}
